/**
 * BoardCard — a unit on the field (ui/31 §2/§3). Reads `instances[occupantId]`
 * (defId + stats) via props; the current/max HP shown here come from the
 * caller's `effectiveStats(state, occupantId)` query (D20/S2) so continuous
 * modifiers are reflected without any logic living in this component.
 */
import { memo, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { registry } from '@cards/cards';
import type { AttackEligibility, CardInstance, CardType, EffectiveStats, PlayerId } from '@cards/engine';
import { CardInfoDialog, CardInfoPopover, type CardInfoData } from './CardInfoPopover.js';
import { CatalogCard } from './CatalogCard.js';
import { cardDescription, cardName, playerDisplayName, signedNumber, type PlayerNames } from './locale.js';
import { CardGlyph } from './CardGlyph.js';
import { CardBackFace } from './CardBackGlyph.js';
import { StatChip } from './StatChip.js';
import { PixelIcon, PixelSymbol } from './PixelIcon.js';
import { ScrollingCardName } from './ScrollingCardName.js';
import { useTurnPlaybackSelector } from './TurnPlayback.js';
import { currentPlaybackTiming } from './playbackSpeed.js';
import type { AttachmentView } from './m14View.js';
import { cardStatusFamily, STATUS_FAMILY_TONES, type CardStatusFamily, type CardStatusView } from './cardStatus.js';
import { useDesktopHover } from './useDesktopHover.js';
import { FULL_CARD_HOVER_OPEN_DELAY_MS, useHoverPopover } from './useHoverPopover.js';
import { useCardTilt } from './useCardTilt.js';
import { useCountTween } from './useCountTween.js';
import { useBoardCarry } from './boardCarry.js';
import { propsEqualByValue } from './memoEquality.js';

export interface BoardCardProps {
  instance: CardInstance;
  effective: EffectiveStats;
  cardType?: CardType;
  keywords?: readonly string[];
  /**
   * Whether a click on the card may open its description — the keyboard-reachable
   * half only (no hand selection, no targeting session). A carried card suppresses
   * it too, but through context, so that a pickup does not change one prop on
   * every card on the board: see ./boardCarry.ts.
   */
  detailsEnabled?: boolean;
  rush: boolean;
  shield: number;
  eligibility: AttackEligibility;
  controllerId?: PlayerId;
  viewerId?: PlayerId;
  playerNames?: PlayerNames | undefined;
  attachments?: readonly AttachmentView[];
  statuses?: readonly CardStatusView[];
  /** Combined cell-state card describing every location effect on this cell. */
  cellStateCard?: CardInfoData;
  /** A released drag ghost is still travelling to this authoritative card. */
  flightHidden?: boolean;
  /** The ghost parked and is dissolving over the now-visible card. */
  flightLanding?: boolean;
}

function statDeltaTone(delta: number | undefined): 'positive' | 'negative' | undefined {
  if (delta === undefined || delta === 0) return undefined;
  return delta > 0 ? 'positive' : 'negative';
}

/**
 * The ±N a merged stat change puts over the badge it moved (ui/31 §3). Rising
 * out of its own stat is what says "+1 к урону" without spending a label on it.
 */
function StatDelta({ delta, testId }: { delta: number | undefined; testId: string }): ReactElement | null {
  if (delta === undefined || delta === 0) return null;
  return (
    <span
      className="cards-card-stat-delta"
      data-testid={testId}
      data-negative={delta < 0 ? 'true' : undefined}
      aria-hidden="true"
    >
      {signedNumber(delta)}
    </span>
  );
}

/**
 * Digit count of a stat figure, capped at 3. The compact mobile field card has
 * ~27px for both numbers, so three-digit values take a smaller step to stay
 * inside the card — see `.cards-card-stat-value[data-len="3"]`. Staged cards,
 * flight ghosts and the absorb ghost tag their figures the same way, or a swap
 * between them would resize the strip mid-animation.
 */
export function statLen(value: number): number {
  return Math.min(3, String(value).length);
}

/** Where a combat value stands against the card's printed one (02 §3.3 «Стат изменён»). */
function statChange(value: number, base: number | undefined): 'up' | 'down' | undefined {
  if (base === undefined || value === base) return undefined;
  return value > base ? 'up' : 'down';
}

/** The colour of a status family (04 §2.1), used for the status rows in the card details. */

function BoardCardComponent({ instance, effective, cardType, keywords = [], detailsEnabled = true, rush, shield, eligibility, controllerId = instance.ownerId, viewerId, playerNames, attachments = [], statuses = [], cellStateCard, flightHidden = false, flightLanding = false }: BoardCardProps): ReactElement {
  // Read at event time only — a carry starting or ending does not re-render this
  // card, which is exactly what it is for. See ./boardCarry.ts.
  const carry = useBoardCarry();
  // Per-card playback slice: the card re-renders only when a cue starts or
  // stops concerning it, not on every step of the turn replay.
  const playback = useTurnPlaybackSelector((visuals) => {
    const combatStrike = visuals.combatCue?.sourceInstanceId === instance.id
      ? visuals.combatCue
      : visuals.combatCue?.reciprocal?.sourceInstanceId === instance.id
        ? visuals.combatCue.reciprocal
        : visuals.combatCue?.parallel?.sourceInstanceId === instance.id
          ? visuals.combatCue.parallel
        : undefined;
    const combatHit = visuals.combatCue?.targetInstanceId === instance.id
      || visuals.combatCue?.reciprocal?.targetInstanceId === instance.id
      || visuals.combatCue?.parallel?.targetInstanceId === instance.id;
    const effectAnimation = visuals.effectCue?.targetInstanceId === instance.id
      ? `${visuals.effectCue.kind}-${visuals.effectCue.phase}`
      : undefined;
    return {
      hackCandidate: visuals.hackCue?.candidates.some(
        (candidate) => candidate.target.kind === 'unit' && candidate.target.instanceId === instance.id,
      ) === true,
      hackSelected: visuals.hackCue?.selected?.target.kind === 'unit'
        && visuals.hackCue.selected.target.instanceId === instance.id,
      randomPickCandidate: visuals.randomPickCue?.candidates.some(
        (candidate) => candidate.instanceId === instance.id,
      ) === true,
      randomPickSelected: visuals.randomPickCue?.selected?.instanceId === instance.id,
      faceDown: visuals.faceDownIds.includes(instance.id),
      lineupHandoff: visuals.lineupHandoffIds.includes(instance.id),
      fractured: visuals.fracturedIds.includes(instance.id),
      // This card is flipping up in the cell of a host it just ate: its reveal
      // becomes the trap's shut instead of the generic entrance pop.
      devouring: visuals.hostAbsorbCue?.phase === 'swallow'
        && visuals.hostAbsorbCue.cellId === instance.cellId,
      effectAnimation,
      skipChanged: visuals.skipChanges?.instanceIds.includes(instance.id) === true,
      melting: visuals.meltingId === instance.id,
      assembling: visuals.assemblyCue?.fragmentIds.includes(instance.id) === true,
      effectDelta: visuals.effectCue?.targetInstanceId === instance.id ? visuals.effectCue.delta : undefined,
      // A merged stat change lands on the badges themselves, so which stat moved
      // is answered by position instead of by a number floating over the art.
      statDeltas: visuals.effectCue?.targetInstanceId === instance.id
        ? visuals.effectCue.statDeltas
        : undefined,
      animation:
        visuals.assemblyCue?.fragmentIds.includes(instance.id) === true
          ? 'assembling'
          : visuals.consumingIds.includes(instance.id)
          ? 'consuming'
          : visuals.transformingId === instance.id
            ? 'transforming'
            : visuals.timedDestroyingId === instance.id
              ? 'timed-destroy'
              : visuals.dyingId === instance.id
                ? 'dying'
                : visuals.enteringId === instance.id
                    // Decay bursts fly several fragments at once, so entering
                    // is also derived from the multi-target spawn cue.
                    || visuals.spawnCue?.targetInstanceIds.includes(instance.id) === true
                  ? (instance.isToken === true ? 'token-enter' : 'entering')
                  : combatStrike?.phase === 'travel'
                    ? 'attacking'
                  : combatStrike?.phase === 'impact'
                    ? 'attack-recover'
                  : visuals.combatCue?.phase === 'impact' && combatHit
                    ? 'hit'
                    : effectAnimation !== undefined
                      ? effectAnimation
                    // A devour/absorb dresses BOTH of its ends itself
                    // (cards-card-devourer, cards-board-card-absorb-*). The
                    // generic source pulse below is a bare data-animation, so it
                    // outranks those class-only rules on specificity and would
                    // silently replace the dedicated keyframe with a 420 ms blink.
                    : visuals.effectPathCue?.phase === 'travel'
                        && visuals.effectPathCue.kind !== 'devour'
                        && visuals.effectPathCue.kind !== 'absorb'
                        && instance.cellId === visuals.effectPathCue.sourceCellId
                      ? 'effect-positive'
                      : undefined,
    };
  });
  const desktopHover = useDesktopHover();
  // The roll has to land inside the beat that explains it: a tween outliving
  // its window would still be counting up while playback moved on.
  const statBeat = currentPlaybackTiming().statBuff;
  const shownAttack = useCountTween(effective.attack, statBeat);
  // Engine damage is intentionally allowed to cross below zero before the
  // death sweep. That intermediate value is useful to rules processing but is
  // not a meaningful player-facing health total.
  const displayedHealth = Math.max(0, effective.health);
  const shownHealth = useCountTween(displayedHealth, statBeat);
  const baseStats = registry.get(instance.defId)?.baseStats;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const {
    value: hoverAnchor,
    closing: hoverClosing,
    open: openHover,
    cancelClose: cancelHoverClose,
    scheduleClose: scheduleHoverClose,
    closeNow: closeHoverNow,
  } = useHoverPopover<HTMLElement>({ openDelayMs: FULL_CARD_HOVER_OPEN_DELAY_MS });
  const cardRef = useRef<HTMLDivElement | null>(null);
  const tilt = useCardTilt();
  const description = cardDescription(instance.defId);
  const preparing = eligibility === 'not_ready';
  const activeState: { id: string; label: string; description: string; family: CardStatusFamily }[] = [];
  if ((instance.heat ?? 0) > 0) activeState.push({
    id: 'heat', label: `Перегрев ${instance.heat}`,
    description: `При Перегреве 4 карта расплавится.`,
    family: 'heat',
  });
  if ((instance.freeze ?? 0) > 0) activeState.push({
    id: 'freeze', label: `Заморозка ${instance.freeze}`,
    description: eligibility === 'frozen'
      ? `Пропустит ближайших боёв: ${instance.freeze}.`
      : 'Сейчас карте разрешено атаковать несмотря на Заморозку.',
    family: 'freeze',
  });
  if (shield > 0) activeState.push({ id: 'shield', label: `Щит ${shield}`, description: `Поглотит следующие ${shield} единиц урона.`, family: 'defense' });
  if (eligibility === 'not_ready') activeState.push({ id: 'delay', label: 'Подготовка', description: 'Сможет атаковать со следующего боя.', family: 'limit' });
  if (eligibility === 'prohibited') activeState.push({ id: 'prohibited', label: 'Не атакует', description: 'Карта не совершает обычные атаки.', family: 'limit' });
  if (instance.hack !== undefined) activeState.push({ id: 'hack', label: 'Взлом', description: 'Следующая атака выберет случайную цель среди контроллера и соседних союзных карт.', family: 'rival' });
  const ownerName = playerDisplayName(instance.ownerId, viewerId, playerNames);
  const controllerName = playerDisplayName(controllerId, viewerId, playerNames);
  if (controllerId !== instance.ownerId) activeState.push({ id: 'controller', label: 'Чужой контроль', description: `Владелец: ${ownerName}. Текущий контроллер: ${controllerName}.`, family: 'rival' });
  for (const attachment of attachments) activeState.push({
    id: `attachment-${attachment.id}`,
    label: attachment.defId === undefined ? 'Прикреплённый вирус' : cardName(attachment.defId),
    description: attachment.defId === undefined ? 'К карте прикреплён активный вирус.' : cardDescription(attachment.defId) ?? 'К карте прикреплён активный вирус.',
    family: 'rival',
  });
  for (const status of statuses) activeState.push({ id: status.id, label: status.label, description: status.description, family: cardStatusFamily(status) });
  const hoverData: CardInfoData = {
    defId: instance.defId,
    ...(cardType === undefined ? {} : { type: cardType }),
    ...(instance.isToken === true ? { token: true } : {}),
    attack: effective.attack,
    health: displayedHealth,
    maxHealth: effective.maxHealth,
    keywords,
    statuses: activeState.map((status) => ({
      id: status.id,
      label: status.label,
      description: status.description,
      color: STATUS_FAMILY_TONES[status.family].face,
    })),
  };
  const { hackCandidate, hackSelected, randomPickCandidate, randomPickSelected, faceDown, lineupHandoff, fractured, effectAnimation, animation, devouring } = playback;
  const attackDelta = playback.statDeltas?.attack;
  const healthDelta = playback.statDeltas?.health;

  useEffect(() => {
    if (!detailsEnabled) {
      setDetailsOpen(false);
      closeHoverNow();
    }
  }, [detailsEnabled, closeHoverNow]);
  // A carry does not re-render this card — that is what the gate buys — so a
  // description that is already open when one starts has to be closed from a
  // listener rather than from the effect above.
  useEffect(() => carry.subscribe(() => {
    if (!carry.carrying) return;
    setDetailsOpen(false);
    closeHoverNow();
  }), [carry, closeHoverNow]);

  function closeDetails(): void {
    setDetailsOpen(false);
  }

  return (
    <>
      <div
      ref={(element) => {
        cardRef.current = element;
        tilt.ref(element);
      }}
      className="cards-board-card cards-tilt"
      onPointerMove={tilt.onPointerMove}
      onPointerLeave={tilt.onPointerLeave}
      onPointerDown={tilt.onPointerDown}
      onPointerUp={tilt.onPointerUp}
      onPointerCancel={tilt.onPointerCancel}
      data-testid={`board-card-${instance.id}`}
      data-card-type={cardType}
      data-animation={animation}
      data-devouring={devouring ? 'true' : undefined}
      data-face-down={faceDown ? 'true' : undefined}
      data-lineup-handoff={lineupHandoff ? 'true' : undefined}
      data-death-phase={fractured ? 'fractured' : undefined}
      data-effect-animation={effectAnimation}
      data-attack-eligibility={eligibility}
      data-hack-candidate={hackCandidate ? 'true' : undefined}
      data-hack-selected={hackSelected ? 'true' : undefined}
      data-random-pick-candidate={randomPickCandidate ? 'true' : undefined}
      data-random-pick-selected={randomPickSelected ? 'true' : undefined}
      data-token={instance.isToken ? 'true' : undefined}
      data-wounded={displayedHealth < effective.maxHealth ? 'true' : undefined}
      data-skip-changed={playback.skipChanged ? 'true' : undefined}
      data-frozen={(instance.freeze ?? 0) > 0 ? 'true' : undefined}
      data-death-style={playback.melting ? 'melt' : undefined}
      data-preparing={preparing ? 'true' : undefined}
      data-flight-hidden={flightHidden && !flightLanding ? 'true' : undefined}
      data-flight-landing={flightLanding ? 'true' : undefined}
      role={detailsEnabled ? 'button' : undefined}
      tabIndex={detailsEnabled ? 0 : undefined}
      aria-haspopup={detailsEnabled ? 'dialog' : undefined}
      aria-label={`${detailsEnabled ? 'Открыть описание карты ' : ''}${cardName(instance.defId)}. Атака: ${effective.attack}. Здоровье: ${displayedHealth} из ${effective.maxHealth}.`}
      title={desktopHover ? undefined : description === undefined ? cardName(instance.defId) : `${cardName(instance.defId)} — ${description}`}
      onClick={detailsEnabled ? (event) => {
        // A release over the board is a drop, not a question — and the carry is
        // only knowable here, at event time (see ./boardCarry.ts). The click is
        // left to bubble in that case, exactly as it did when this handler was
        // absent altogether.
        if (carry.carrying) return;
        event.stopPropagation();
        if (!desktopHover) setDetailsOpen(true);
      } : undefined}
      onMouseEnter={detailsEnabled && desktopHover
        ? (event) => {
          if (carry.carrying) return;
          openHover(event.currentTarget);
        }
        : undefined}
      onMouseLeave={detailsEnabled ? () => scheduleHoverClose() : undefined}
      onKeyDown={detailsEnabled ? (event) => {
        // I opens a card from anywhere on the board, like Enter/Space (Q6).
        if (event.key === 'Enter' || event.key === ' ' || (event.code === 'KeyI' && !event.ctrlKey && !event.metaKey && !event.altKey)) {
          // The same gate the click uses — see there. Near-unreachable mid-carry,
          // but the two ways into this dialog must agree on when it may open.
          if (carry.carrying) return;
          event.preventDefault();
          event.stopPropagation();
          setDetailsOpen(true);
        }
      } : undefined}
    >
      <span className="cards-tilt-glare" aria-hidden="true" />
      {faceDown ? (
        <CardBackFace patternId={`board-card-${instance.id}-back-pattern`} hidden />
      ) : null}
      <div className="cards-board-card-art" aria-hidden="true">
        <CardGlyph defId={instance.defId} size={60} />
      </div>
      <ScrollingCardName
        name={cardName(instance.defId)}
        className="cards-board-card-name"
        trackClassName="cards-board-card-name-track"
      />
      {playback.effectDelta !== undefined && playback.effectDelta !== 0 ? (
        <span
          className="cards-board-card-playback-delta"
          data-negative={playback.effectDelta < 0 ? 'true' : undefined}
          // Перегрев and Заморозка count in their family's colour (03 §4.4).
          data-tone={effectAnimation?.startsWith('heat-') ? 'heat' : effectAnimation?.startsWith('freeze-') ? 'freeze' : undefined}
          aria-hidden="true"
        >
          {signedNumber(playback.effectDelta)}
        </span>
      ) : null}
      {rush || (instance.heat ?? 0) > 0 || (instance.freeze ?? 0) > 0 || shield > 0 || eligibility === 'not_ready' || eligibility === 'prohibited' || instance.hack !== undefined || statuses.length > 0 ? (
        <div className="cards-board-card-statuses">
          {rush ? <span data-tone="warning" data-testid={`board-card-${instance.id}-rush`} title="Натиск: может атаковать в раунд раскрытия" aria-label="Натиск"><PixelIcon name="rush" /></span> : null}
          {(instance.heat ?? 0) > 0 ? <span data-tone="heat" data-testid={`board-card-${instance.id}-heat`} data-level={Math.min(instance.heat ?? 0, 3)} title={`Перегрев ${instance.heat}: при 4 карта расплавится`} aria-label={`Перегрев ${instance.heat}`}><PixelIcon name="heat" />{instance.heat}</span> : null}
          {(instance.freeze ?? 0) > 0 ? <span data-tone="freeze" data-testid={`board-card-${instance.id}-freeze`} title={eligibility === 'frozen' ? `Заморозка ${instance.freeze}: пропустит столько ближайших боёв` : `Заморозка ${instance.freeze}; сейчас карте разрешено атаковать`} aria-label={`Заморозка ${instance.freeze}`}><PixelIcon name="freeze" />{instance.freeze}</span> : null}
          {shield > 0 ? <span data-tone="defense" data-testid={`board-card-${instance.id}-shield`} title={`Щит ${shield}: поглотит столько следующего урона`} aria-label={`Щит ${shield}`}><PixelIcon name="shield" />{shield}</span> : null}
          {eligibility === 'not_ready' ? <span data-tone="limit" data-testid={`board-card-${instance.id}-delay`} title="Задержка: сможет атаковать со следующего боя" aria-label="Задержка атаки"><PixelIcon name="delay" /></span> : null}
          {eligibility === 'prohibited' ? <span data-tone="limit" data-testid={`board-card-${instance.id}-prohibited`} title="Эта карта не может атаковать" aria-label="Не может атаковать"><PixelIcon name="prohibited" /></span> : null}
          {instance.hack !== undefined ? (
            <span
              data-tone="rival"
              data-testid={`board-card-${instance.id}-hack`}
              title={`Взлом #${instance.hack.seq}: следующая атака выберет случайную цель среди игрока и соседних карт`}
              aria-label="Взлом"
            >
              <PixelIcon name="hack" />
            </span>
          ) : null}
          {statuses.filter((status) => status.compact !== false).map((status) => (
            <span
              key={status.id}
              data-testid={`board-card-${instance.id}-status-${status.id}`}
              title={`${status.label}: ${status.description}`}
              aria-label={status.label}
              data-tone={cardStatusFamily(status)}
            >
              {status.icon !== undefined ? <PixelIcon name={status.icon} /> : null}<PixelSymbol symbol={status.symbol} />
            </span>
          ))}
        </div>
      ) : null}
      {controllerId !== instance.ownerId || attachments.length > 0 ? (
        <div className="cards-board-card-m14-markers">
          {controllerId !== instance.ownerId ? (
            <span
              data-tone="rival"
              data-testid={`board-card-${instance.id}-controller`}
              title={`Владелец: ${ownerName}; текущий контроллер: ${controllerName}`}
              aria-label={`Под контролем: ${controllerName}`}
            >
              <PixelIcon name="controller" />
            </span>
          ) : null}
          {attachments.map((attachment) => (
            <span
              key={attachment.id}
              data-tone="rival"
              data-testid={`board-card-${instance.id}-attachment-${attachment.id}`}
              title={attachment.defId === undefined
                ? 'Прикреплённый вирус'
                : `${cardName(attachment.defId)} — ${cardDescription(attachment.defId) ?? 'Прикреплённый вирус'}`}
              aria-label="Прикреплённый вирус"
            >
              <PixelIcon name="attachment" />
            </span>
          ))}
        </div>
      ) : null}
      <div className="cards-board-card-stats">
        <StatChip
          icon="attack"
          value={shownAttack}
          len={statLen(shownAttack)}
          valueHidden
          data-testid={`board-card-${instance.id}-damage`}
          data-delta={statDeltaTone(attackDelta)}
          data-stat-change={statChange(effective.attack, baseStats?.attack)}
          aria-label={`Атака: ${effective.attack}`}
          title={`Атака: ${effective.attack}`}
        >
          <StatDelta delta={attackDelta} testId={`board-card-${instance.id}-damage-delta`} />
        </StatChip>
        <StatChip
          icon="health"
          value={shownHealth}
          len={statLen(shownHealth)}
          valueHidden
          data-testid={`board-card-${instance.id}-health`}
          data-delta={statDeltaTone(healthDelta)}
          data-stat-change={statChange(effective.maxHealth, baseStats?.health)}
          aria-label={`Здоровье: ${displayedHealth} из ${effective.maxHealth}`}
          title={`Здоровье: ${displayedHealth} из ${effective.maxHealth}`}
        >
          <StatDelta delta={healthDelta} testId={`board-card-${instance.id}-health-delta`} />
        </StatChip>
      </div>
      </div>
      {hoverAnchor !== undefined && detailsEnabled && desktopHover ? (
        <CardInfoPopover
          anchor={hoverAnchor}
          data={hoverData}
          closing={hoverClosing}
          {...(cellStateCard === undefined ? {} : { supplementData: cellStateCard })}
          testId={`board-card-${instance.id}-popover`}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={scheduleHoverClose}
        />
      ) : null}
      {detailsOpen ? (
        <CardInfoDialog
          data={hoverData}
          testId={`board-card-${instance.id}-detail`}
          onClose={closeDetails}
          returnFocusRef={cardRef}
          {...(cellStateCard === undefined ? {} : {
            supplement: <CatalogCard data={cellStateCard} testId={`board-card-${instance.id}-detail-effects`} />,
          })}
        />
      ) : null}
    </>
  );
}

/**
 * Every prop here is game-state-derived plain JSON, so value equality is what
 * matters.
 *
 * It saves the gesture boundaries only because the carry no longer arrives as a
 * prop: it used to ride `detailsEnabled`, which Cell forwards straight through,
 * so a pickup and a release each failed all 18 comparisons legitimately and
 * re-rendered every card mid-drag. See ./boardCarry.ts.
 *
 * What it buys the rest of the time is a Cell re-rendering for reasons of its own
 * — its per-cell playback subscription (the face-down lineup, a cell cue), a
 * staged pile growing underneath, a hover popover opening — where the occupant did
 * not change and its subtree (glyph, stat strip with its tweens, status list,
 * popover data) is the expensive half of the cell.
 */
export const BoardCard = memo(BoardCardComponent, propsEqualByValue);
